import { useState, useEffect } from 'react';
import { SidebarData } from './SidebarData';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import '../App.css';
import './Sidebar.css';

function Sidebar() {
  const { isAuthenticated, userInfo } = useAuth();
  const role = userInfo?.role;

  const [absenceCount, setAbsenceCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const getCustomerFeatures = () => {
    return userInfo?.features || userInfo?.customer_features || {};
  };

  const hasFeature = (featureKey) => {
    if (!featureKey) return true;

    // Eigenaar beheert customers/modules en mag menu-items met feature altijd zien
    // als zo'n item ooit voor eigenaar beschikbaar wordt gemaakt.
    if (role === 'eigenaar') return true;

    const features = getCustomerFeatures();
    return features?.[featureKey] === true;
  };

  const isMenuItemVisible = (item) => {
    if (!item.roles.includes(role)) return false;
    if (!hasFeature(item.feature)) return false;

    if (
      item.title === 'Locaties' &&
      role === 'super_admin' &&
      userInfo?.is_single_user
    ) {
      return false;
    }

    return true;
  };

  const isActiveLink = (link) => {
    const currentPath = window.location.pathname;

    return (
      currentPath === link ||
      (link !== '/' && currentPath.startsWith(`${link}/`))
    );
  };

  useEffect(() => {
    if (!role) {
      setAbsenceCount(0);
      return;
    }

    const rolesForAbsence = ['super_admin', 'locatie_admin', 'docent'];

    if (!rolesForAbsence.includes(role)) {
      setAbsenceCount(0);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        const token = sessionStorage.getItem('jwtToken');
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/absence/unreadcount`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );

        setAbsenceCount(res.data?.count || 0);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Fout bij ophalen afwezigheids-aantal:', err);
        }
      }
    })();

    return () => controller.abort();
  }, [role]);

  useEffect(() => {
    if (!role) {
      setUnreadCount(0);
      return;
    }

    const rolesWithMsg = [
      'ouder',
      'docent',
      'super_admin',
      'locatie_admin',
      'eigenaar',
    ];

    if (!rolesWithMsg.includes(role)) {
      setUnreadCount(0);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        const token = sessionStorage.getItem('jwtToken');
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/messages/unreadcount`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );

        setUnreadCount(res.data?.count || 0);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Fout bij ophalen unread berichten:', err);
        }
      }
    })();

    return () => controller.abort();
  }, [role]);

  if (!isAuthenticated || !userInfo) return null;

  return (
    <aside className="Sidebar" aria-label="Hoofdnavigatie">
      <nav className="Sidebar-nav">
        <ul className="SidebarList">
          {SidebarData.filter(isMenuItemVisible).map((val) => {
            let badgeCount;

            if (val.link === '/absences') {
              badgeCount = absenceCount;
            } else if (val.link.startsWith('/messages')) {
              badgeCount = unreadCount;
            }

            const isActive = isActiveLink(val.link);
            const visibleBadgeCount = badgeCount > 99 ? '99+' : badgeCount;

            return (
              <li key={val.link} className="Sidebar-row">
                <a
                  className={`Sidebar-link ${isActive ? 'is-active' : ''}`}
                  href={val.link}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="Sidebar-icon" aria-hidden="true">
                    <i className={val.icon}></i>
                  </span>

                  <span className="Sidebar-title">{val.title}</span>

                  {badgeCount > 0 && (
                    <span
                      className="Sidebar-badge"
                      aria-label={`${badgeCount} ongelezen`}
                    >
                      {visibleBadgeCount}
                    </span>
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

export default Sidebar;